import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';

import { ConfirmationDialog } from './ConfirmationDialog';

export interface ConfirmationDialogOptions {
  okLabel?: ReactNode;
  cancelLabel?: ReactNode;
  title?: ReactNode;
  text?: ReactNode;
}

export interface UseConfirmationDialogReturn {
  dialog: ReactNode;
  open: () => void;
  confirmation: () => Promise<unknown>;
  configure: (options: ConfirmationDialogOptions) => void;
}

export const useConfirmationDialog = ({
  okLabel,
  cancelLabel,
  title,
  text,
}: ConfirmationDialogOptions): UseConfirmationDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const resolve = useRef<((value: boolean) => void) | undefined>();
  const [innerTitle, setInnerTitle] = useState(title);
  const [innerText, setInnerText] = useState(text);
  const [innerOkLabel, setInnerOkLabel] = useState(okLabel);
  const [innerCancelLabel, setInnerCancelLabel] = useState(cancelLabel);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const confirmation = useCallback(async () => {
    return new Promise(res => {
      resolve.current = res;
    });
  }, []);

  const onOk = useCallback(() => {
    if (resolve.current) resolve.current(true);

    close();
  }, [close]);

  const onCancel = useCallback(() => {
    if (resolve.current) resolve.current(false);

    close();
  }, [close]);

  const configure = useCallback((options: ConfirmationDialogOptions) => {
    if (options.cancelLabel) setInnerCancelLabel(options.cancelLabel);
    if (options.okLabel) setInnerOkLabel(options.okLabel);
    if (options.text) setInnerText(options.text);
    if (options.title) setInnerTitle(options.title);
  }, []);

  const dialog = useMemo((): ReactNode => {
    return (
      <ConfirmationDialog
        open={isOpen}
        onClose={close}
        okLabel={innerOkLabel}
        cancelLabel={innerCancelLabel}
        title={innerTitle}
        text={innerText}
        onOk={onOk}
        onCancel={onCancel}
      />
    );
  }, [
    close,
    innerCancelLabel,
    innerOkLabel,
    innerText,
    innerTitle,
    isOpen,
    onCancel,
    onOk,
  ]);

  return {
    dialog,
    open,
    confirmation,
    configure,
  };
};
